import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Grid,
  Stack,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import Loading from "../../../main/components/Loading";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import { clientsApi } from "../../../services/api";
import { Client } from "../types";
import { ClientDetailsHeader } from "./ClientDetailsHeader";
import { ConfirmationModal } from "../../../main/components/ConfirmationModal";
import { ActionAlert } from "../../../main/components/alerts/ActionAlert";
import PlanDetailsModal from "./PlanDetailsModal";

export default function ClientDetailsHeaderPage(props: { client: Client | null }) {
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [screensNumber, setScreensNumber] = useState<number>(0);
  const [initialScreensNumber, setInitialScreensNumber] = useState<number>(0);
  const [isScreensNumberChanged, setIsScreensNumberChanged] =
    useState<boolean>(false);
  const [
    openEditConfirmationScreensModal,
    setOpenEditConfirmationScreensModal,
  ] = useState<boolean>(false);
  const [showPlanDetailsModal, setShowPlanDetailsModal] =
    useState<boolean>(false);

  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);

  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const path = window.location.pathname;
  const clientId = path.split("/")[2];

  const onMount = async () => {
    setLoading(true);
    try {
      const clientResponse = await clientsApi.getClient(clientId);
      setClient(clientResponse);
      const initialScreens =
        clientResponse?.permittedSimultaneousConnections || 0;
      setScreensNumber(initialScreens);
      setInitialScreensNumber(initialScreens);
    } catch (error) {
      throw new Error("Erro ao carregar cliente");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    onMount();
  }, []);

  const onConfirm = async () => {
    setOpenEditConfirmationScreensModal(false);
    if (client) {
      await clientsApi.pathClientConnections(client.id, screensNumber);
    }
  };

  const handleFocus = () => {
    setIsScreensNumberChanged(true);
  };

  const handleBlur = () => {
    if (screensNumber === initialScreensNumber) {
      setIsScreensNumberChanged(false);
    }
  };

  const clientPlan = client?.plan?.name;

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <Grid container>
          <Grid item xs={12}>
            <Stack flexDirection="row" justifyContent="space-between">
              <ClientDetailsHeader
                client={client}
                setClient={setClient}
                onMount={onMount}
              />
              {!isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    marginLeft: { md: "20px" },
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: "left",
                      fontSize: { xs: "13px", md: "16px" },
                      marginBottom: "5px",
                    }}
                  >
                    Número de Telas Simultâneas :
                  </Typography>
                  <Stack flexDirection="row">
                    <TextField
                      size="small"
                      inputProps={{
                        min: 0,
                        max: client?.plan?.maxSimultaneousConnections,
                      }}
                      value={screensNumber}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        var value = parseInt(e.target.value);
                        if (
                          client?.plan &&
                          value >
                          client?.plan.maxSimultaneousConnections
                        )
                          value =
                            client.plan.maxSimultaneousConnections;
                        if (value < 0) value = 0;
                        setScreensNumber(value);
                        if (value !== initialScreensNumber) {
                          setIsScreensNumberChanged(true);
                        } else {
                          setIsScreensNumberChanged(false);
                        }
                      }}
                      type="number"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          marginLeft: "20px",
                          borderRadius: "4px",
                          height: "35px",
                          width: "75px",
                          padding: "0 6px",
                        },
                        "& .MuiInputBase-input": {
                          height: "30px",
                          padding: "0 10px",
                          textAlign: "center",
                          marginLeft: "10px",
                        },
                      }}
                    />

                    {isScreensNumberChanged && (
                      <PrimaryBaseButton
                        sx={{ height: "30px", marginLeft: "20px" }}
                        title="Salvar"
                        onClick={() =>
                          setOpenEditConfirmationScreensModal(true)
                        }
                      />
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              paddingBottom: "8px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginTop: "-10px",
              }}
            >
              <Typography>Plano: {clientPlan}</Typography>
              <Typography
                sx={{ cursor: "pointer" }}
                onClick={() => setShowPlanDetailsModal(true)}
              >
                <InfoIcon sx={{ marginLeft: "10px", color: primaryColor }} />
              </Typography>
            </Box>
            {isMobile && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "row", md: "column" },
                  alignItems: "center",
                  marginTop: "10px",
                }}
              >
                <Typography
                  sx={{
                    textAlign: "left",
                    fontSize: { xs: "13px", md: "16px" },
                    marginBottom: "5px",
                  }}
                >
                  Número de Telas Simultâneas :
                </Typography>
                <Stack flexDirection="row">
                  <TextField
                    size="small"
                    inputProps={{
                      min: 0,
                      max: client?.plan?.maxSimultaneousConnections,
                    }}
                    value={screensNumber}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      var value = parseInt(e.target.value);
                      if (
                        client?.plan &&
                        value >
                        client.plan.maxSimultaneousConnections
                      )
                        value =
                          client.plan.maxSimultaneousConnections;
                      if (value < 0) value = 0;
                      setScreensNumber(value);
                      if (value !== initialScreensNumber) {
                        setIsScreensNumberChanged(true);
                      } else {
                        setIsScreensNumberChanged(false);
                      }
                    }}
                    type="number"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        marginLeft: "20px",
                        borderRadius: "4px",
                        height: "35px",
                        width: "75px",
                        padding: "0 10px",
                      },
                      "& .MuiInputBase-input": {
                        height: "30px",
                        padding: "0 10px",
                        textAlign: "center",
                        marginLeft: { xs: "0px", md: "10px" },
                      },
                    }}
                  />

                  {isScreensNumberChanged && (
                    <PrimaryBaseButton
                      sx={{ height: "30px", marginLeft: "20px" }}
                      title="Salvar"
                      onClick={() => setOpenEditConfirmationScreensModal(true)}
                    />
                  )}
                </Stack>
              </Box>
            )}
          </Grid>
        </Grid>
      )}
      <PlanDetailsModal
        plan={props.client?.plan}
        setShowPlanDetailsModal={setShowPlanDetailsModal}
        showPlanDetailsModal={showPlanDetailsModal}
      />
      <ConfirmationModal
        open={openEditConfirmationScreensModal}
        setOpen={setOpenEditConfirmationScreensModal}
        title="Salvar novo número de telas"
        contentText="deseja mesmo salvar o novo número de telas ?"
        onConfirm={onConfirm}
        setOpenErrorModal={setOpenErrorModal}
        setOpenSuccessModal={setOpenSuccessModal}
      />
      <ActionAlert
        message="O número de telas foi alterado com sucesso"
        severity="success"
        open={openSuccessModal}
        setOpen={setOpenSuccessModal}
        onClose={() => {
          setOpenSuccessModal(false);
          onMount();
        }}
      />
      <ActionAlert
        message="Ocorreu um erro ao alterar o número de telas"
        severity="error"
        open={openErrorModal}
        setOpen={setOpenErrorModal}
        onClose={() => {
          setOpenErrorModal(false);
        }}
      />
    </>
  );
}
